import api from '../api';
import {
  formatAddress,
  getFullAddress,
  formatStatusDisplay,
  formatPatientName,
  getDisplayValue,
  extractNestedValue,
  determineEventStatus,
  calculateAge,
} from './formatters';
import {
  getEmptyPatientStats,
  getEmptyScreeningStats,
  getEmptyEventStats,
  getEmptyRiskAnalysis,
  getEmptyTrendsData,
  getEmptyVolunteerData,
  getEmptyQueueStats,
  getEmptyDetailedData,
  getEmptyQueueAnalytics,
} from './emptyStates';
import {
  formatPatientData,
  processPatientDataForDisplay,
  extractEventStatistics,
  calculatePatientStats,
  assessPatientRisk,
} from './patientAnalytics';
import {
  processQueueDataForAnalytics,
  formatQueueAnalyticsForDisplay,
  extractEventAnalyticsFromQueue,
} from './queueAnalytics';
import {
  getLocationSearchString,
  getEventSearchString,
  getPatientSearchString,
  filterScreeningData,
  getScreeningFilterOptions,
  formatScreeningDataForDisplay,
} from './searchFilters';

/**
 * Analytics Service
 * Provides comprehensive analytics data for the BHA Reports & Analytics dashboard
 */
class AnalyticsService {
  // ─── API Methods ────────────────────────────────────────────────

  async getDashboardAnalytics(filters = {}) {
    try {
      const [
        patientStats,
        screeningStats,
        eventStats,
        riskAnalysis,
        trendsData,
        volunteerData
      ] = await Promise.all([
        this.getPatientStatistics(filters),
        this.getScreeningStatistics(filters),
        this.getEventStatistics(filters),
        this.getRiskAnalysis(filters),
        this.getTrendsData(filters),
        this.getVolunteerData(filters)
      ]);

      return {
        success: true,
        data: {
          overview: {
            totalPatients: patientStats.total,
            totalScreenings: screeningStats.total,
            activeEvents: eventStats.active,
            highRiskDetected: riskAnalysis.highRisk,
            riskDistribution: riskAnalysis.distribution,
            monthlyGrowth: trendsData.growth
          },
          patientStats,
          screeningStats,
          eventStats,
          riskAnalysis,
          trendsData,
          volunteerData
        }
      };
    } catch (error) {
      console.error('Error getting dashboard analytics:', error);
      throw error;
    }
  }

  async getPatientStatistics(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/patients', {
        params: filters
      });
      
      if (response.data && Array.isArray(response.data)) {
        const processedPatients = processPatientDataForDisplay(response.data);
        const eventStats = extractEventStatistics(response.data);
        
        return {
          ...calculatePatientStats(processedPatients),
          patients: processedPatients,
          eventBreakdown: eventStats.events,
          locationBreakdown: eventStats.locations
        };
      }
      
      return response.data || getEmptyPatientStats();
    } catch (error) {
      console.error('Error fetching patient statistics (no fallback mock will be used):', error);
      return getEmptyPatientStats();
    }
  }

  async getScreeningStatistics(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/screenings', {
        params: filters
      });
      return response.data || getEmptyScreeningStats();
    } catch (error) {
      console.error('Error fetching screening statistics:', error);
      return getEmptyScreeningStats();
    }
  }

  async getEventStatistics(filters = {}) {
    try {
      const enhancedStats = await this.getEnhancedEventStatistics(filters);
      return enhancedStats;
    } catch (error) {
      console.error('Error fetching event statistics:', error);
      return getEmptyEventStats();
    }
  }

  async getRiskAnalysis(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/risk-analysis', {
        params: filters
      });
      return response.data || getEmptyRiskAnalysis();
    } catch (error) {
      console.error('Error fetching risk analysis:', error);
      return getEmptyRiskAnalysis();
    }
  }

  async getTrendsData(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/trends', {
        params: filters
      });
      return response.data || getEmptyTrendsData();
    } catch (error) {
      console.error('Error fetching trends data:', error);
      return getEmptyTrendsData();
    }
  }

  async getVolunteerData(filters = {}) {
    try {
      const response = await api.get('/user-management/analytics/volunteers', {
        params: filters
      });
      return response.data || getEmptyVolunteerData();
    } catch (error) {
      console.error('Error fetching volunteer data:', error);
      return getEmptyVolunteerData();
    }
  }

  async getQueueStatistics(eventId = null) {
    try {
      const enhancedStats = await this.getEnhancedQueueStatistics(eventId);
      if (enhancedStats.enhanced) {
        return enhancedStats;
      }

      const endpoint = eventId ? `/screening/queue/${eventId}` : '/screening/queue';
      const response = await api.get(endpoint, {
        params: { statsOnly: true }
      });
      return response.stats || getEmptyQueueStats();
    } catch (error) {
      console.error('Error fetching queue statistics:', error);
      return getEmptyQueueStats();
    }
  }

  async getDetailedScreeningData(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/detailed-data', {
        params: filters
      });
      
      if (response.data && response.data.success) {
        const { data } = response.data;
        
        const patients = Array.isArray(data.patients) ? data.patients : [];
        const screenings = Array.isArray(data.screenings) ? data.screenings : [];
        const queueData = Array.isArray(data.queueData) ? data.queueData : [];
        
        const combinedData = [...patients, ...queueData];
        
        return {
          patients: processPatientDataForDisplay(combinedData),
          screenings: processPatientDataForDisplay(screenings),
          queueData: processPatientDataForDisplay(queueData),
          rawData: data,
          exportTimestamp: data.exportTimestamp || new Date().toISOString(),
          totalRecords: combinedData.length
        };
      } else if (response.data) {
        let patients = [];
        
        if (Array.isArray(response.data)) {
          patients = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          patients = response.data.data;
        } else if (response.data.patients && Array.isArray(response.data.patients)) {
          patients = response.data.patients;
        } else {
          patients = [response.data];
        }
        
        return {
          patients: processPatientDataForDisplay(patients),
          screenings: [],
          queueData: [],
          exportTimestamp: new Date().toISOString(),
          totalRecords: patients.length
        };
      }
      
      return getEmptyDetailedData();
    } catch (error) {
      console.error('Error fetching detailed screening data:', error);
      return getEmptyDetailedData();
    }
  }

  async getEnhancedQueueStatistics(eventId = null) {
    try {
      const endpoint = eventId ? `/screening/queue/${eventId}` : '/screening/queue';
      const response = await api.get(endpoint);
      
      if (response.data && Array.isArray(response.data)) {
        const queueAnalytics = processQueueDataForAnalytics(response.data);
        const formattedAnalytics = formatQueueAnalyticsForDisplay(queueAnalytics);
        
        return {
          ...getEmptyQueueStats(),
          enhanced: true,
          analytics: formattedAnalytics,
          rawData: response.data
        };
      }
      
      if (response.stats) {
        return {
          ...response.stats,
          enhanced: false
        };
      }
      
      return getEmptyQueueStats();
    } catch (error) {
      console.error('Error fetching enhanced queue statistics:', error);
      return getEmptyQueueStats();
    }
  }

  async getEnhancedEventStatistics(filters = {}) {
    try {
      const queueResponse = await api.get('/screening/queue');
      
      if (queueResponse.data && Array.isArray(queueResponse.data)) {
        const eventStats = extractEventAnalyticsFromQueue(queueResponse.data);
        return {
          ...eventStats,
          enhanced: true,
          source: 'queue_data'
        };
      }

      const response = await api.get('/events/analytics/statistics', {
        params: filters
      });
      
      return {
        ...(response.data || getEmptyEventStats()),
        enhanced: false,
        source: 'events_endpoint'
      };
    } catch (error) {
      console.error('Error fetching enhanced event statistics:', error);
      return {
        ...getEmptyEventStats(),
        enhanced: false,
        source: 'empty_fallback'
      };
    }
  }

  async getFormattedDetailedScreeningData(filters = {}) {
    try {
      const response = await api.get('/screening/analytics/detailed-data', {
        params: filters
      });
      
      if (response.data) {
        return {
          ...response.data,
          patients: formatScreeningDataForDisplay(response.data.patients || []),
          screenings: formatScreeningDataForDisplay(response.data.screenings || []),
          exportTimestamp: new Date().toISOString(),
          filterOptions: getScreeningFilterOptions(response.data.patients || response.data.screenings || [])
        };
      }
      
      return getEmptyDetailedData();
    } catch (error) {
      console.error('Error fetching formatted detailed screening data:', error);
      return getEmptyDetailedData();
    }
  }

  // ─── Delegating Wrappers (backward compatibility) ───────────────

  formatPatientData(patientData) { return formatPatientData(patientData); }
  processPatientDataForDisplay(patients) { return processPatientDataForDisplay(patients); }
  extractEventStatistics(patients) { return extractEventStatistics(patients); }
  calculatePatientStats(patients) { return calculatePatientStats(patients); }
  assessPatientRisk(patient) { return assessPatientRisk(patient); }
  formatAddress(address) { return formatAddress(address); }
  getFullAddress(address) { return getFullAddress(address); }
  formatStatusDisplay(status) { return formatStatusDisplay(status); }
  formatPatientName(screening) { return formatPatientName(screening); }
  getDisplayValue(value, type) { return getDisplayValue(value, type); }
  extractNestedValue(data, path, fallback) { return extractNestedValue(data, path, fallback); }
  determineEventStatus(eventData) { return determineEventStatus(eventData); }
  calculateAge(dateOfBirth) { return calculateAge(dateOfBirth); }
  processQueueDataForAnalytics(queueData) { return processQueueDataForAnalytics(queueData); }
  formatQueueAnalyticsForDisplay(queueAnalytics) { return formatQueueAnalyticsForDisplay(queueAnalytics); }
  extractEventAnalyticsFromQueue(queueData) { return extractEventAnalyticsFromQueue(queueData); }
  getLocationSearchString(location) { return getLocationSearchString(location); }
  getEventSearchString(event) { return getEventSearchString(event); }
  getPatientSearchString(patient) { return getPatientSearchString(patient); }
  filterScreeningData(screenings, searchTerm, filters) { return filterScreeningData(screenings, searchTerm, filters); }
  getScreeningFilterOptions(screenings) { return getScreeningFilterOptions(screenings); }
  formatScreeningDataForDisplay(screenings) { return formatScreeningDataForDisplay(screenings); }

  // ─── Empty State Fallbacks ──────────────────────────────────────

  getEmptyPatientStats() { return getEmptyPatientStats(); }
  getEmptyScreeningStats() { return getEmptyScreeningStats(); }
  getEmptyEventStats() { return getEmptyEventStats(); }
  getEmptyRiskAnalysis() { return getEmptyRiskAnalysis(); }
  getEmptyTrendsData() { return getEmptyTrendsData(); }
  getEmptyVolunteerData() { return getEmptyVolunteerData(); }
  getEmptyQueueStats() { return getEmptyQueueStats(); }
  getEmptyDetailedData() { return getEmptyDetailedData(); }
  getEmptyQueueAnalytics() { return getEmptyQueueAnalytics(); }
}

export default new AnalyticsService();
